import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { Check, AlertTriangle, Eye, EyeOff, RefreshCw } from 'lucide-react';
import { api } from '../api/client';
import { PageHeader, LoadingState, Btn } from '../components/ui';

interface AiFeatureCfg {
  model: string;
  thinkingBudget: number | null;
}

interface PlatformAiConfig {
  riskEvaluation: AiFeatureCfg;
  insightGeneration: AiFeatureCfg;
  ticketSummary: AiFeatureCfg;
  statusUpdate: AiFeatureCfg;
  actionGeneration: AiFeatureCfg;
  assetGeneration: AiFeatureCfg;
}

interface PlatformSettings {
  maintenanceMode: boolean;
  selfServiceSignupEnabled: boolean;
  defaultPlan: string;
  aiConfig?: PlatformAiConfig;
  geminiApiKeyMasked?: string | null;
}

interface AiModel {
  id: string;
  displayName: string;
  supportsThinking: boolean;
}

const DEFAULT_MODEL = 'gemini-flash-latest';

const DEFAULT_ASSET_MODEL = 'gemini-pro-latest';

const DEFAULT_AI_CONFIG: PlatformAiConfig = {
  riskEvaluation:    { model: DEFAULT_MODEL, thinkingBudget: null },
  insightGeneration: { model: DEFAULT_MODEL, thinkingBudget: null },
  ticketSummary:     { model: DEFAULT_MODEL, thinkingBudget: null },
  statusUpdate:      { model: DEFAULT_MODEL, thinkingBudget: null },
  actionGeneration:  { model: DEFAULT_MODEL, thinkingBudget: null },
  assetGeneration:   { model: DEFAULT_ASSET_MODEL, thinkingBudget: null },
};

const AI_FEATURES: { key: keyof PlatformAiConfig; label: string; description: string }[] = [
  { key: 'riskEvaluation',    label: 'Risk Evaluation',    description: 'Per-project risk scoring across 6 facets' },
  { key: 'insightGeneration', label: 'Daily Insights',     description: "What's on deck portfolio briefing" },
  { key: 'ticketSummary',     label: 'Ticket Summaries',   description: 'One-line AI summary per ticket' },
  { key: 'statusUpdate',      label: 'Status Updates',     description: 'Generated project status reports' },
  { key: 'actionGeneration',  label: 'Suggested Actions',  description: 'Context-aware actions assembled per project, ticket, and role' },
  { key: 'assetGeneration',   label: 'Asset Generation',   description: 'PRD docs, slides, and other generated assets — uses thinking model for higher quality output' },
];

async function fetchModels(): Promise<AiModel[]> {
  const data = await api.get<{ models: AiModel[] }>('/ai/models');
  return data.models;
}

function Toggle({
  label,
  description,
  checked,
  onChange,
  danger,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  danger?: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-4 py-3 border-b border-white/[0.06] last:border-0">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          {danger && checked && <AlertTriangle className="h-3.5 w-3.5 text-amber-400 flex-shrink-0" />}
          <span className="text-sm font-medium text-gray-200">{label}</span>
        </div>
        {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
      </div>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 transition-colors focus:outline-none ${
          checked
            ? danger
              ? 'bg-amber-500 border-amber-500'
              : 'bg-primary border-primary'
            : 'bg-white/10 border-transparent'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
            checked ? 'translate-x-4' : 'translate-x-0'
          }`}
        />
      </button>
    </div>
  );
}

function AiFeatureRow({
  featureKey,
  label,
  description,
  cfg,
  models,
  onChange,
}: {
  featureKey: keyof PlatformAiConfig;
  label: string;
  description: string;
  cfg: AiFeatureCfg;
  models: AiModel[];
  onChange: (patch: Partial<AiFeatureCfg>) => void;
}) {
  const selectedModel = models.find(m => m.id === cfg.model);
  const thinkingEnabled = cfg.thinkingBudget !== null;

  return (
    <div className="py-3 border-b border-white/[0.06] last:border-0 space-y-2">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-200">{label}</p>
          <p className="text-xs text-gray-500">{description}</p>
        </div>
        <div className="flex-shrink-0 w-64">
          <select
            value={cfg.model}
            onChange={e => onChange({ model: e.target.value, thinkingBudget: null })}
            className="w-full bg-gray-900 border border-white/[0.08] rounded-lg px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-primary/50 transition-colors"
          >
            {models.length === 0 && <option value={cfg.model}>{cfg.model}</option>}
            {models.map(m => (
              <option key={m.id} value={m.id}>{m.displayName}</option>
            ))}
          </select>
        </div>
      </div>

      {(selectedModel?.supportsThinking || thinkingEnabled) && (
        <div className="flex items-center gap-4 pl-0">
          <label className="flex items-center gap-2 cursor-pointer">
            <button
              type="button"
              onClick={() => onChange({ thinkingBudget: thinkingEnabled ? null : 1024 })}
              className={`relative inline-flex h-4 w-7 rounded-full transition-colors ${
                thinkingEnabled ? 'bg-primary' : 'bg-white/10'
              }`}
            >
              <span className={`inline-block h-3 w-3 mt-0.5 transform rounded-full bg-white transition-transform ${
                thinkingEnabled ? 'translate-x-3.5' : 'translate-x-0.5'
              }`} />
            </button>
            <span className="text-xs text-gray-400">Thinking</span>
          </label>
          {thinkingEnabled && (
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={0}
                max={32768}
                step={256}
                value={cfg.thinkingBudget ?? 1024}
                onChange={e => onChange({ thinkingBudget: Math.max(0, Math.min(32768, parseInt(e.target.value) || 0)) })}
                className="w-20 bg-white/[0.04] border border-white/[0.08] rounded-lg px-2 py-1 text-xs text-white text-center focus:outline-none focus:border-primary/50"
              />
              <span className="text-xs text-gray-500">tokens (0 = dynamic)</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function PlatformSettings() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<PlatformSettings>({
    queryKey: ['admin', 'platform-settings'],
    queryFn: () => api.get('/platform-settings'),
  });

  const { data: models = [], isLoading: modelsLoading, isError: modelsError, refetch: refetchModels } = useQuery<AiModel[]>({
    queryKey: ['admin', 'ai-models'],
    queryFn: fetchModels,
    staleTime: 0,           // always recheck on mount — server caches for 5 min
    refetchOnWindowFocus: true,
    retry: 1,
  });

  const [form, setForm] = useState<Partial<PlatformSettings>>({});
  const [aiConfig, setAiConfig] = useState<PlatformAiConfig>(DEFAULT_AI_CONFIG);
  const [geminiApiKey, setGeminiApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (data) {
      setForm(data);
      setAiConfig({
        riskEvaluation:    { ...DEFAULT_AI_CONFIG.riskEvaluation,    ...(data.aiConfig?.riskEvaluation ?? {}) },
        insightGeneration: { ...DEFAULT_AI_CONFIG.insightGeneration, ...(data.aiConfig?.insightGeneration ?? {}) },
        ticketSummary:     { ...DEFAULT_AI_CONFIG.ticketSummary,     ...(data.aiConfig?.ticketSummary ?? {}) },
        statusUpdate:      { ...DEFAULT_AI_CONFIG.statusUpdate,      ...(data.aiConfig?.statusUpdate ?? {}) },
        actionGeneration:  { ...DEFAULT_AI_CONFIG.actionGeneration,  ...(data.aiConfig?.actionGeneration ?? {}) },
        assetGeneration:   { ...DEFAULT_AI_CONFIG.assetGeneration,   ...(data.aiConfig?.assetGeneration ?? {}) },
      });
    }
  }, [data]);

  const save = useMutation({
    mutationFn: () => {
      const payload: Record<string, unknown> = {
        maintenanceMode: form.maintenanceMode,
        selfServiceSignupEnabled: form.selfServiceSignupEnabled,
        defaultPlan: form.defaultPlan,
        aiConfig,
      };
      // Only include key if admin typed something (empty = don't touch)
      if (geminiApiKey !== '') payload.geminiApiKey = geminiApiKey;
      return api.put('/platform-settings', payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'platform-settings'] });
      setGeminiApiKey(''); // clear after save
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    },
  });

  const updateFeature = (key: keyof PlatformAiConfig, patch: Partial<AiFeatureCfg>) => {
    setAiConfig(prev => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  };

  if (isLoading) return <LoadingState />;

  return (
    <div>
      <PageHeader title="Platform Settings" subtitle="Global configuration affecting all tenants" />

      <div className="max-w-2xl space-y-6">
        {/* General toggles */}
        <div>
          <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">General</h2>
          <div className="bg-bg-surface rounded-xl border border-white/[0.06] px-4 py-1">
            <Toggle
              label="Maintenance Mode"
              description="Blocks all non-admin API requests with a 503."
              checked={form.maintenanceMode ?? false}
              onChange={v => setForm(f => ({ ...f, maintenanceMode: v }))}
              danger
            />
            <Toggle
              label="Self-Service Signup"
              description="Allows new users to create their own entity on signup."
              checked={form.selfServiceSignupEnabled ?? false}
              onChange={v => setForm(f => ({ ...f, selfServiceSignupEnabled: v }))}
            />
          </div>
        </div>

        {/* Default plan */}
        <div>
          <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Billing</h2>
          <div className="bg-bg-surface rounded-xl border border-white/[0.06] px-4 py-3">
            <label className="block text-xs font-medium text-gray-400 mb-1.5">Default Plan</label>
            <input
              type="text"
              value={form.defaultPlan ?? ''}
              onChange={e => setForm(f => ({ ...f, defaultPlan: e.target.value }))}
              placeholder="free"
              className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-primary/50 transition-colors"
            />
            <p className="text-xs text-gray-500 mt-1">Applied when a new entity is created without an explicit plan.</p>
          </div>
        </div>

        {/* AI Configuration */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">AI Configuration</h2>
            <button
              onClick={() => void refetchModels()}
              title="Refresh model list"
              className="text-gray-500 hover:text-gray-300 transition-colors p-1 rounded"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${modelsLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>
          {modelsError && (
            <p className="text-xs text-amber-400 bg-amber-900/20 border border-amber-900/30 rounded-lg px-3 py-2 mb-3">
              Could not load model list — save your Google AI API key above first, then click refresh.
            </p>
          )}

          {/* API Key */}
          <div className="bg-bg-surface rounded-xl border border-white/[0.06] px-4 py-3 mb-3">
            <label className="block text-xs font-medium text-gray-400 mb-1.5">Google AI API Key</label>
            {data?.geminiApiKeyMasked && (
              <p className="text-xs text-gray-500 mb-1.5">
                Current key: <span className="font-mono text-gray-300">{data.geminiApiKeyMasked}</span>
              </p>
            )}
            <div className="relative">
              <input
                type={showKey ? 'text' : 'password'}
                value={geminiApiKey}
                onChange={e => setGeminiApiKey(e.target.value)}
                placeholder={data?.geminiApiKeyMasked ? 'Enter new key to replace…' : 'AIza...'}
                className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-3 py-2 pr-10 text-sm text-white placeholder-gray-600 font-mono focus:outline-none focus:border-primary/50 transition-colors"
              />
              <button
                type="button"
                onClick={() => setShowKey(s => !s)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
              >
                {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">Leave blank to keep the existing key. Used by all AI features platform-wide.</p>
          </div>

          {/* Per-feature model config */}
          <div className="bg-bg-surface rounded-xl border border-white/[0.06] px-4 py-1">
            {AI_FEATURES.map(f => (
              <AiFeatureRow
                key={f.key}
                featureKey={f.key}
                label={f.label}
                description={f.description}
                cfg={aiConfig[f.key]}
                models={models}
                onChange={patch => updateFeature(f.key, patch)}
              />
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Platform defaults. Individual tenants can override per-feature in their own settings.
          </p>
        </div>

        {/* Save */}
        <div className="flex items-center gap-3">
          <Btn
            variant="primary"
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Saving…' : 'Save changes'}
          </Btn>
          {saved && (
            <span className="flex items-center gap-1 text-xs text-emerald-400">
              <Check className="h-3.5 w-3.5" />
              Saved
            </span>
          )}
          {save.isError && (
            <span className="text-xs text-red-400">{(save.error as Error).message}</span>
          )}
        </div>
      </div>
    </div>
  );
}
